import { Select, Spin } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { searchAllGames } from 'src/api/metaverse'


const PAGE_SIZE = 20

const SEARCH_FIELD = 'name'

interface GameItem {
  id: string
  title: string
}

interface GameOption {
  label: string
  value: string
}

interface GameSearchSelectProps {
  value?: string
  className?: string
  style?: React.CSSProperties
  placeholder?: string
  onChange?: (value?: string) => void
  
  items?: GameItem[] | null
}

const toOption = (g: GameItem): GameOption => ({
  label: g.title,
  
  value: g.title.trim(),
})

const GameSearchSelect: React.FC<GameSearchSelectProps> = ({
  value,
  className,
  style,
  placeholder = 'Select Game',
  onChange,
  items,
}) => {
  const categoryMode = Array.isArray(items)

  const [fetched, setFetched] = useState<GameOption[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const pageRef = useRef(0)
  const searchRef = useRef('')
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  const loadGames = async ({ reset = false } = {}) => {
    try {
      setLoading(true)
      const page = reset ? 0 : pageRef.current

      const params: Record<string, any> = { page, pageSize: PAGE_SIZE }
     
      if (searchRef.current) params[SEARCH_FIELD] = searchRef.current

      const data = await searchAllGames(params)

      const list: GameItem[] = data?.data ?? data ?? []
      const next: GameOption[] = list.map(toOption)

      setFetched((prev) => (reset ? next : [...prev, ...next]))
      pageRef.current = page + 1
      setHasMore(next.length === PAGE_SIZE)
    } catch (error) {
      console.error('Failed to load games:', error)
    } finally {
      setLoading(false)
    }
  }

  
  useEffect(() => {
    if (categoryMode) return
    pageRef.current = 0
    searchRef.current = ''
    setHasMore(true)
    loadGames({ reset: true })
    return () => clearTimeout(debounceRef.current)
  }, [categoryMode])

 
  const handleSearch = (term: string) => {
    if (categoryMode) return 
    searchRef.current = term
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      pageRef.current = 0
      loadGames({ reset: true })
    }, 600)
  }

 
  const handlePopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (categoryMode || loading || !hasMore) return
    const el = e.currentTarget
    if (el.scrollTop + el.offsetHeight >= el.scrollHeight - 50) {
      loadGames()
    }
  }

  const handleClear = () => {
    if (categoryMode) return 
    searchRef.current = ''
    pageRef.current = 0
    loadGames({ reset: true })
   
  }

 
  const clientFilter = (input: string, option?: { label?: string }) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

  const options = categoryMode ? (items as GameItem[]).map(toOption) : fetched

  return (
    <Select
      showSearch
      allowClear
      value={value}
      placeholder={placeholder}
      className={className}
      style={style}
      options={options}
      
      filterOption={categoryMode ? clientFilter : false}
      loading={categoryMode ? false : loading}
      onChange={onChange}
      onSearch={handleSearch}
      onClear={handleClear}
      onPopupScroll={handlePopupScroll}
      notFoundContent={!categoryMode && loading ? <Spin size="small" /> : null}
    />
  )
}

export default GameSearchSelect
